import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MapPin, Navigation } from "lucide-react";

const fmtTL = (v) => `₺${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const PAY = { cash: "Nakit", card: "POS", transfer: "Havale", veresiye: "Veresiye" };

function loadLeaflet() {
  return new Promise((resolve) => {
    if (window.L) return resolve(window.L);
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const sc = document.createElement("script");
    sc.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    sc.onload = () => resolve(window.L);
    sc.onerror = () => resolve(null);
    document.body.appendChild(sc);
  });
}

export default function VanMap() {
  const [vehicles, setVehicles] = useState([]);
  const [vid, setVid] = useState("");
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layer = useRef(null);

  useEffect(() => { api.get("/van-sales/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/van-sales/reports/map", { params: { date, vehicle_id: vid || undefined } })
      .then((r) => setData(r.data)).catch(() => setData({ points: [] })).finally(() => setLoading(false));
  }, [date, vid]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = await loadLeaflet();
      if (cancelled || !L || !mapRef.current) return;
      if (!mapObj.current) {
        mapObj.current = L.map(mapRef.current).setView([39.0, 35.0], 6);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(mapObj.current);
      }
      if (layer.current) { layer.current.remove(); }
      layer.current = L.layerGroup().addTo(mapObj.current);
      const pts = data?.points || [];
      const latlngs = [];
      pts.forEach((p) => {
        const m = L.marker([p.lat, p.lng]).addTo(layer.current);
        m.bindPopup(`<b>${p.customer || ""}</b><br>${p.time} · ${fmtTL(p.gross)}<br>${PAY[p.payment_method] || ""} · ${p.doc_no}`);
        latlngs.push([p.lat, p.lng]);
      });
      if (latlngs.length) {
        if (latlngs.length > 1) L.polyline(latlngs, { color: "#0f7a3d", weight: 2, dashArray: "5,6" }).addTo(layer.current);
        mapObj.current.fitBounds(latlngs, { padding: [40, 40], maxZoom: 15 });
      }
      setTimeout(() => mapObj.current && mapObj.current.invalidateSize(), 200);
    })();
    return () => { cancelled = true; };
  }, [data]);

  const pts = data?.points || [];

  return (
    <div className="space-y-4" data-testid="van-map">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[180px]">
          <Label className="text-xs">Araç</Label>
          <select data-testid="map-vehicle" value={vid} onChange={(e) => setVid(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value="">Tüm araçlar</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Tarih</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="map-date" className="mt-1" /></div>
        <div className="flex items-center gap-1 pb-1 text-sm text-slate-500"><Navigation className="h-4 w-4 text-brand-green" /> {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `${pts.length} ziyaret`}</div>
      </div>

      <div ref={mapRef} data-testid="map-canvas" className="h-[420px] w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm z-0" />

      {pts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">
          Bu gün için konumlu satış yok. (Fiş oluştururken tarayıcı konum izni verilirse noktalar burada görünür.)
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400">
              <tr><th className="px-3 py-2">Saat</th><th>Müşteri</th><th>Araç</th><th>Ödeme</th><th className="text-right">Tutar</th></tr>
            </thead>
            <tbody>
              {pts.map((p, i) => (
                <tr key={i} className="border-t border-slate-50" data-testid={`map-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{p.time}</td>
                  <td className="flex items-center gap-1 font-medium text-slate-800"><MapPin className="h-3 w-3 text-brand-green" /> {p.customer || "—"}</td>
                  <td className="text-slate-500">{p.vehicle_plate}</td>
                  <td className="text-slate-500">{PAY[p.payment_method] || p.payment_method}</td>
                  <td className="text-right font-semibold text-slate-800">{fmtTL(p.gross)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
